/**
 * Плагин: захват лока на mousedown, снятие после клика без жеста и при клике мимо,
 * запрет жестов по чужим объектам, откат при deny.
 */
export default {
	init(modeler, context) {
		const {graph, onLock, viewIndex} = context;
		const VIEW_INDEX = viewIndex ?? 0;
		const localAcquired = new Set();

		const kindOf = cell => {
			if (cell && typeof cell.isEdge === 'function' && cell.isEdge()) return 'connection';
			return 'node';
		};

		const viewIdOf = () => modeler.getActiveViewId?.() ?? null;

		const isGesturing = objectId => Boolean(modeler._geometryGestureIds?.has(objectId));

		const captureGeometry = cell => {
			if (!cell?.id) return;
			const viewElement = modeler._findViewElementById(cell.id);
			if (kindOf(cell) === 'connection') {
				const vertices = cell.getVertices?.() ?? [];
				modeler.captureCommittedGeometry?.(cell.id, {
					kind: 'connection',
					bendpoints: vertices.map(p => ({
						x: Math.round(Number(p.x ?? p.$x ?? 0)),
						y: Math.round(Number(p.y ?? p.$y ?? 0)),
					})),
				});
				return;
			}
			const pos = cell.getPosition?.() ?? {};
			const size = cell.getSize?.() ?? {};
			modeler.captureCommittedGeometry?.(cell.id, {
				kind: 'node',
				x: Math.round(pos.x ?? viewElement?.bounds?.x ?? 0),
				y: Math.round(pos.y ?? viewElement?.bounds?.y ?? 0),
				w: Math.round(size.width ?? viewElement?.bounds?.width ?? viewElement?.w ?? 120),
				h: Math.round(size.height ?? viewElement?.bounds?.height ?? viewElement?.h ?? 60),
			});
		};

		const releaseOne = (objectId, kind) => {
			if (!objectId || typeof onLock !== 'function') return;
			if (isGesturing(objectId)) return;
			if (!localAcquired.has(objectId)) return;
			localAcquired.delete(objectId);
			onLock({
				action: 'release',
				objectId,
				kind: kind || 'node',
				viewIndex: VIEW_INDEX,
				viewId: viewIdOf(),
			});
		};

		const releaseIdle = keepIds => {
			const keep = keepIds instanceof Set ? keepIds : new Set(keepIds ?? []);
			[...localAcquired].forEach(objectId => {
				if (keep.has(objectId) || isGesturing(objectId)) return;
				const cell = graph.getCellById?.(objectId);
				releaseOne(objectId, kindOf(cell));
			});
		};

		const acquireForCell = cell => {
			if (!cell?.id || typeof onLock !== 'function') return;
			if (modeler.isLockedByOther?.(cell.id)) return;
			captureGeometry(cell);
			localAcquired.add(cell.id);
			onLock({
				action: 'acquire',
				objectId: cell.id,
				kind: kindOf(cell),
				viewIndex: VIEW_INDEX,
				viewId: viewIdOf(),
			});
		};

		const descendantNodes = cell => {
			if (!cell || typeof cell.isNode !== 'function' || !cell.isNode()) return [];
			const all = typeof cell.getDescendants === 'function' ? cell.getDescendants({deep: true}) : [];
			return (all || []).filter(item => item && typeof item.isNode === 'function' && item.isNode());
		};

		const ancestorNodes = cell => {
			const list = [];
			let parent = typeof cell?.getParent === 'function' ? cell.getParent() : null;
			while (parent && typeof parent.isNode === 'function' && parent.isNode()) {
				list.push(parent);
				parent = typeof parent.getParent === 'function' ? parent.getParent() : null;
			}
			return list;
		};

		const lockedInFamily = cell => {
			if (!cell) return false;
			return [cell, ...descendantNodes(cell), ...ancestorNodes(cell)].some(
				item => item?.id && modeler.isLockedByOther?.(item.id)
			);
		};

		const blockIfLocked = (cell, e) => {
			if (!lockedInFamily(cell)) return false;
			e?.preventDefault?.();
			e?.stopPropagation?.();
			return true;
		};

		const cellsForPointer = cell => {
			const selected = typeof graph.getSelectedCells === 'function' ? graph.getSelectedCells() : [];
			const group = selected.some(item => item?.id === cell?.id) ? selected : [cell];
			return group.filter(item => item && (item.isNode?.() || item.isEdge?.()));
		};

		const withDescendantNodes = cells => {
			const seen = new Set();
			const result = [];
			const push = item => {
				if (!item?.id || seen.has(item.id)) return;
				seen.add(item.id);
				result.push(item);
			};
			cells.forEach(cell => {
				push(cell);
				descendantNodes(cell).forEach(push);
			});
			return result;
		};

		const onNodeMouseDown = ({e, node}) => {
			if (blockIfLocked(node, e)) return;
			const group = withDescendantNodes(cellsForPointer(node));
			releaseIdle(new Set(group.map(cell => cell.id)));
			group.forEach(acquireForCell);
		};

		const onEdgeMouseDown = ({e, edge}) => {
			if (blockIfLocked(edge, e)) return;
			releaseIdle(new Set([edge.id]));
			acquireForCell(edge);
		};

		const onNodeMouseUp = ({node}) => {
			if (!node?.id) return;
			const releaseIfIdle = cell => {
				if (!cell?.id || isGesturing(cell.id)) return;
				releaseOne(cell.id, 'node');
			};
			if (!isGesturing(node.id)) releaseIfIdle(node);
			descendantNodes(node).forEach(releaseIfIdle);
		};

		const onEdgeMouseUp = ({edge}) => {
			if (!edge?.id || isGesturing(edge.id)) return;
			releaseOne(edge.id, 'connection');
		};

		const onBlankPointer = () => {
			releaseIdle();
		};

		const previousInteracting = graph.options?.interacting;
		if (graph.options && previousInteracting !== false) {
			graph.options.interacting = cellView => {
				if (lockedInFamily(cellView?.cell)) {
					return false;
				}
				return typeof previousInteracting === 'function'
					? previousInteracting(cellView)
					: previousInteracting;
			};
		}

		graph.on('node:mousedown', onNodeMouseDown);
		graph.on('edge:mousedown', onEdgeMouseDown);
		graph.on('node:mouseup', onNodeMouseUp);
		graph.on('edge:mouseup', onEdgeMouseUp);
		graph.on('blank:click', onBlankPointer);
		graph.on('blank:mousedown', onBlankPointer);
		graph.on('blank:mouseup', onBlankPointer);

		return () => {
			if (graph.options) graph.options.interacting = previousInteracting;
			graph.off('node:mousedown', onNodeMouseDown);
			graph.off('edge:mousedown', onEdgeMouseDown);
			graph.off('node:mouseup', onNodeMouseUp);
			graph.off('edge:mouseup', onEdgeMouseUp);
			graph.off('blank:click', onBlankPointer);
			graph.off('blank:mousedown', onBlankPointer);
			graph.off('blank:mouseup', onBlankPointer);
		};
	},
};
